import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["pupilListLoading", "pupilList"];

  connect() {
    this.hideLoading();
    this.togglePretestAssignmentFields();
    this.updateDriveTestInstructorValidation();
  }

  displayLoading() {
    optionalChaining(() =>
      this.targets.find("pupilListLoading").classList.remove("u-hidden")
    );
    optionalChaining(() =>
      this.targets.find("pupilList").classList.add("u-hidden")
    );
  }

  hideLoading() {
    optionalChaining(() =>
      this.targets.find("pupilListLoading").classList.add("u-hidden")
    );
    optionalChaining(() =>
      this.targets.find("pupilList").classList.remove("u-hidden")
    );
  }

  selectedPupilDriveTestPretestDurationMinutes(event = null) {
    const selectedPupil =
      event?.target?.matches?.('input[name="selected-item-pupil"]')
        ? event.target
        : document.querySelector('input[name="selected-item-pupil"]:checked');
    const value = Number(selectedPupil?.dataset?.driveTestPretestDurationMinutes);
    return Number.isNaN(value) ? 0 : value;
  }

  applySelectedPupilDriveTestPretestDuration(event = null) {
    const selectedLessonType =
      document.getElementById("lesson_type")?.value ||
      document.querySelector('[name="lesson_type"]')?.value ||
      "";
    const isDriveTestFlow =
      selectedLessonType === "DriveTest" ||
      document.getElementById("pretest_duration_in_minutes") != null;
    if (!isDriveTestFlow) return;

    const durationInMinutes = this.selectedPupilDriveTestPretestDurationMinutes(event);
    const candidateFields = [
      document.getElementById("new_lesson_specific_duration_in_minutes"),
      document.getElementById("pretest_duration_in_minutes"),
    ].filter(Boolean);

    candidateFields.forEach((field) => {
      if (field.tagName === "SELECT") {
        const preferredValue = String(durationInMinutes);
        const fallbackValue = "0";
        if ([...field.options].some((option) => option.value === preferredValue)) {
          field.value = preferredValue;
        } else if ([...field.options].some((option) => option.value === fallbackValue)) {
          field.value = fallbackValue;
        }
      } else {
        field.value = String(durationInMinutes);
      }
    });

    this.togglePretestAssignmentFields();
    this.updateDriveTestInstructorValidation();
  }

  togglePretestAssignmentFields() {
    const pretestDurationField = document.getElementById("pretest_duration_in_minutes");
    const pretestAssignmentFields = document.getElementById("pretest-assignment-fields");
    const pretestInstructorField = document.getElementById("pretest_instructor_id");
    const pretestVehicleField = document.getElementById("pretest_vehicle_id");

    if (!pretestDurationField || !pretestAssignmentFields) return;

    const shouldHideAssignments = Number(pretestDurationField.value) === 0;
    pretestAssignmentFields.classList.toggle("u-hidden", shouldHideAssignments);

    [pretestInstructorField, pretestVehicleField].forEach((field) => {
      if (!field) return;

      field.disabled = shouldHideAssignments;
    });

    this.updateDriveTestInstructorValidation();
  }

  setLocationSectionHidden(hidden) {
    const wrapper = document.getElementById("booking-location-quicklook-wrapper");
    if (!wrapper) return;

    wrapper.classList.toggle("u-hidden", hidden);
  }

  handlePretestInstructorSelection() {
    this.updateDriveTestInstructorValidation();
  }

  shouldEnforceDriveTestInstructorRule() {
    return document.getElementById("enforce_previous_test_instructor_rule")?.value === "true";
  }

  selectedPupilPreviousDriveTestInstructorIds() {
    const selectedPupil = document.querySelector('input[name="selected-item-pupil"]:checked');
    if (!selectedPupil) return [];

    try {
      const parsed = JSON.parse(selectedPupil.dataset.driveTestPreviousInstructorIds || "[]");
      return Array.isArray(parsed) ? parsed.map((id) => String(id)) : [];
    } catch (error) {
      return [];
    }
  }

  sameInstructorUsedForPretestAndTest() {
    const pretestDurationInMinutes = Number(
      document.getElementById("pretest_duration_in_minutes")?.value || 0
    );
    if (pretestDurationInMinutes <= 0) return false;

    const testInstructorId =
      document.getElementById("slot_instructor_id")?.value ||
      document.getElementById("instructor_id")?.value ||
      "";
    const pretestInstructorId = document.getElementById("pretest_instructor_id")?.value || "";

    return (
      testInstructorId &&
      pretestInstructorId &&
      String(pretestInstructorId) === String(testInstructorId)
    );
  }

  pretestAssignmentRequirementMessage() {
    if (!this.shouldEnforceDriveTestInstructorRule()) return "";

    const pretestDurationInMinutes = Number(
      document.getElementById("pretest_duration_in_minutes")?.value || 0
    );
    if (pretestDurationInMinutes <= 0) return "";

    const pretestInstructorId = document.getElementById("pretest_instructor_id")?.value || "";
    const pretestVehicleId = document.getElementById("pretest_vehicle_id")?.value || "";

    if (!pretestInstructorId && !pretestVehicleId) {
      return "Please select a pre-test instructor and vehicle.";
    }

    if (!pretestInstructorId) {
      return "Please select a pre-test instructor.";
    }

    if (!pretestVehicleId) {
      return "Please select a pre-test vehicle.";
    }

    return "";
  }

  driveTestInstructorValidationMessage() {
    if (!this.shouldEnforceDriveTestInstructorRule()) return "";

    const pretestAssignmentRequirementMessage = this.pretestAssignmentRequirementMessage();
    if (pretestAssignmentRequirementMessage.length > 0) {
      return pretestAssignmentRequirementMessage;
    }

    if (this.sameInstructorUsedForPretestAndTest()) {
      return "Student cannot test with a previous lesson instructor setting is enabled, the pre-test instructor and drive test instructor cannot be the same.";
    }

    const selectedPupil = document.querySelector('input[name="selected-item-pupil"]:checked');
    if (!selectedPupil) return "";

    const testInstructorId =
      document.getElementById("slot_instructor_id")?.value ||
      document.getElementById("instructor_id")?.value ||
      "";

    const previousInstructorIds = this.selectedPupilPreviousDriveTestInstructorIds();
    if (testInstructorId && previousInstructorIds.includes(String(testInstructorId))) {
      return "This student cannot take their drive test with this instructor because they have taught them previously.";
    }

    return "";
  }

  updateDriveTestInstructorValidation() {
    const validationMessage = document.getElementById("drive-test-instructor-validation-message");
    const saveButton = document.getElementById("slot_save_changes");
    const message = this.driveTestInstructorValidationMessage();

    if (validationMessage) {
      if (
        message ===
        "Student cannot test with a previous lesson instructor setting is enabled, the pre-test instructor and drive test instructor cannot be the same."
      ) {
        validationMessage.innerHTML =
          '<span class="u-medium">Student cannot test with a previous lesson instructor</span> setting is enabled, the pre-test instructor and drive test instructor cannot be the same.';
      } else {
        validationMessage.textContent = message;
      }
      validationMessage.classList.toggle("u-hidden", message.length === 0);
    }

    if (saveButton) {
      const isInvalid = message.length > 0;
      saveButton.disabled = isInvalid;
      saveButton.setAttribute("aria-disabled", isInvalid ? "true" : "false");
      saveButton.classList.toggle("is-disabled", isInvalid);
    }
  }

  closeQuickLook(event) {
    event.preventDefault();
    const quickViewElement = document.querySelector(
      "#school-dashboard-open-slot-quick-look"
    );
    quickViewElement.classList.add("u-hidden");
  }

  async showQuickLook(event) {
    event.preventDefault();

    const isLoading = this.data.get("isLoading") === "true";

    if (isLoading) {
      alert("This slot is being loaded, please wait.");
      return;
    }

    const instructorId = this.data.get("instructorId");
    const startTime = this.data.get("startTime");
    const endTime = this.data.get("endTime");
    const slotId = encodeURIComponent(this.data.get("slotId"));
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content;
    const vehicleIds = this.data.get("vehicleIds");
    const zoneIds = this.data.get("zoneIds")
    const isTest = this.data.get("isTest") === "true";
    const testTime =
      this.data.get("testTime") ||
      startTime ||
      new Date().toISOString();

    const response = await fetch(
      `/schools/open-slot/${instructorId}/quick-look/${startTime}/${endTime}?slot_id=${slotId}&vehicle_ids=${vehicleIds}&is_test=${isTest}&test_time=${testTime}&zone_ids=${zoneIds}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": csrfToken,
          Accept: "text/vnd.turbo-stream.html",
        },
      }
    );

    if (response.ok) {
      const quickViewElement = document.querySelector(
        "#school-dashboard-lesson-quick-look"
      );
      quickViewElement.classList.remove("u-hidden");

      const responseBody = await response.text();
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(responseBody, "text/html");
      const template = xmlDoc.querySelector("template");
      const targetElement = document.getElementById("school_lesson_quick_look");

      if (template && targetElement) {
        targetElement.innerHTML = "";
        targetElement.appendChild(template.content.cloneNode(true));
      }
    }
  }

  setInstructor(event) {
    // from the  data-id html tag, get the value
    const lessonId = event.target.dataset.id;

    // set the value of student_lesson_premium_instructor_profile_id to the instructorId
    document.getElementById(
      "premium_instructor_profile_student_lesson_id"
    ).value = lessonId;

    // find all the items that start with the ID selected-item-instructor
    const selectedItems = document.querySelectorAll(
      '[id^="selected-item-instructor"]'
    );

    // selectedItems.forEach((item) => {
    //   if (item.dataset.id == instructorId) {
    //     item.value = true
    //   }
    //   else {
    //     item.value = false
    //   }
    // })
  }

  // Used by the method below to keep track of the timeout so it casn be cancelled.
  studentSearchTimeout = null;
  async studentSearchList(event) {
    event.preventDefault();
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content;
    const searchTerm = event.target.value;

    if (this.studentSearchTimeout) {
      clearTimeout(this.studentSearchTimeout);
    }

    this.displayLoading();

    this.studentSearchTimeout = setTimeout(async () => {
      const response = await fetch(
        `/schools/open-slot/filter-book-slot-students?search_term=${searchTerm}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            "X-CSRF-Token": csrfToken,
            Accept: "text/vnd.turbo-stream.html",
          },
        }
      );

      if (response.ok) {
        const stream = await response.text();
        Turbo.renderStreamMessage(stream);
      }

      this.hideLoading();
    }, 500);
  }

  async loadMorePupils(event) {
    event.preventDefault();
    const button = event.currentTarget;
    
    if (button.disabled) {
      return;
    }
    
    const offset = parseInt(button.dataset.offset || button.getAttribute('data-offset') || '10', 10);
    const searchTerm = button.dataset.searchTerm || button.getAttribute('data-search-term') || '';
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content;

    const originalText = button.textContent;
    const buttonId = button.id;
    
    const openSlotWrapper = document.getElementById('school_open_slot_quick_look_load_more_wrapper');
    const lessonWrapper = document.getElementById('school_lesson_quick_look_load_more_wrapper');
    const isLessonContext = !!lessonWrapper && !openSlotWrapper;
    
    button.disabled = true;
    button.textContent = 'Loading...';

    try {
      let url;
      let pupilsListId;
      let wrapperId;
      
      if (isLessonContext) {
        url = searchTerm 
          ? `/schools/lesson/add-lesson/quick-look/pupil-list?offset=${offset}&search_term=${encodeURIComponent(searchTerm)}`
          : `/schools/lesson/add-lesson/quick-look/pupil-list?offset=${offset}`;
        pupilsListId = 'school_lesson_quick_look_pupils_list';
        wrapperId = 'school_lesson_quick_look_load_more_wrapper';
      } else {
        url = searchTerm 
          ? `/schools/open-slot/filter-book-slot-students?search_term=${encodeURIComponent(searchTerm)}&search_offset=${offset}`
          : `/schools/open-slot/load-more-pupils?offset=${offset}`;
        pupilsListId = 'school_open_slot_quick_look_book_slot_pupils_list';
        wrapperId = 'school_open_slot_quick_look_load_more_wrapper';
      }
      
      const response = await fetch(
        url,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            "X-CSRF-Token": csrfToken,
            Accept: "text/vnd.turbo-stream.html",
          },
        }
      );

      if (response.ok) {
        const stream = await response.text();
        
        if (!stream || stream.trim().length === 0) {
          console.error('Empty turbo stream response');
          button.textContent = originalText;
          button.disabled = false;
          return;
        }
        
        try {
          Turbo.renderStreamMessage(stream);
          
          setTimeout(() => {
            const wrapper = document.getElementById(wrapperId);
            if (wrapper) {
              const newButton = wrapper.querySelector('button');
              if (newButton && newButton.disabled) {
                newButton.disabled = false;
                newButton.textContent = 'Load more students';
              }
            } else {
              const originalButton = document.getElementById(buttonId);
              if (originalButton && originalButton.disabled) {
                originalButton.textContent = originalText;
                originalButton.disabled = false;
              }
            }
          }, 300);
        } catch (streamError) {
          console.error('Error rendering turbo stream:', streamError);
          button.textContent = originalText;
          button.disabled = false;
        }
      } else {
        const errorText = await response.text().catch(() => 'Unknown error');
        console.error('HTTP error loading more pupils:', response.status, errorText);
        button.textContent = originalText;
        button.disabled = false;
      }
    } catch (error) {
      console.error('Error loading more pupils:', error);
      button.textContent = originalText;
      button.disabled = false;
    }
  }

  async setInstructorAndLocation(event) {
    const loadingState = this.startLoadingState(event.currentTarget);
    const pupilId = event.target.dataset.id;

    this.applySelectedPupilDriveTestPretestDuration(event);
    this.updateDriveTestInstructorValidation();

    if (this.driveTestInstructorValidationMessage().length > 0) {
      this.setLocationSectionHidden(true);
      this.finishLoadingState(loadingState);
      return;
    }

    this.setLocationSectionHidden(false);

    // "Do you think God stays in heaven because he, too, lives in fear of what he's created here on earth?"
    let url = `/us/schools/quick-look/${pupilId}/set-instructor-and-location`;
    let hideInstructor = !!(
      document.getElementById("hide_instructor") &&
      document.getElementById("hide_instructor").value
    );
    let hideLocation = "false";

    // This feels like a Hack to not show instructor when the classroom type is being set.
    if (document.getElementById("select_open_classroom_type")) {
      if (document.getElementById("select_open_classroom_type").checked) {
        hideInstructor = "true";
        hideLocation = "true";
      }
    }

    const pickedInstructorId =
      document.getElementById("picked_instructor_id") &&
      document.getElementById("picked_instructor_id").value;
    const slotInstructorId =
      document.getElementById("slot_instructor_id") &&
      document.getElementById("slot_instructor_id").value;
    const slotStartTime =
      document.getElementById("slot_start_time") &&
      document.getElementById("slot_start_time").value;
    const queryParams = [
      { name: "instructor_token", value: pickedInstructorId },
      { name: "hide_instructor", value: hideInstructor },
      { name: "hide_location", value: hideLocation },
      { name: "slot_instructor_id", value: slotInstructorId },
      { name: "slot_start_time", value: slotStartTime },
    ];
    const query = queryParams
      .filter((param) => param.value)
      .map((param) => `${param.name}=${param.value}`)
      .join("&");
    if (query) url += `?${query}`;

    const csrfToken = document.querySelector('meta[name="csrf-token"]').content;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": csrfToken,
          Accept: "text/vnd.turbo-stream.html",
        },
      });

      if (response.ok) {
        const stream = await response.text();
        Turbo.renderStreamMessage(stream);
        var element = document.getElementById(
          "school_quick_look_add_lesson_instructor_and_location_form"
        );

        if (element && hideLocation == "false") {
          element.removeAttribute("hidden");
        }

        if (element && hideLocation == "true") {
          element.setAttribute("hidden", true);
        }

        const classroomSection = document.getElementById("schedule-classroom-program-form");
        if (classroomSection) {
          const classroomFrame = classroomSection.querySelector("turbo-frame");
          if (classroomFrame) {
            const frameUrl = new URL("/schools/classroom/program/quick-look/schedule", window.location.origin);
            frameUrl.searchParams.set("picked_pupil", pupilId);
            classroomFrame.innerHTML = '<div class="u-textCenter p2"><span class="icon icon-spinner2 u-animateSpin u-textGreenMid u-inlineBlock"></span> Loading</div>';
            classroomFrame.setAttribute("src", frameUrl.toString());
          }
        }
      }
    } finally {
      this.finishLoadingState(loadingState);
    }
  }

  async reserveSlot(event) {
    event.preventDefault();
    const loadingState = this.startLoadingState(event.currentTarget || this.element);
    // const url = this.element.getAttribute('href');
    const url = new URL(
      this.element.getAttribute("href"),
      window.location.origin
    );
    const params = new URLSearchParams(url.search);

    const startTime = params.get("start_time");
    const endTime = params.get("end_time");
    const instructorId = params.get("instructor_id");

    try {
      const response = await fetch(this.element.getAttribute("href"), {
        method: "POST",
        body: JSON.stringify({
          reserved_slot: true,
          start_time: startTime,
          end_time: endTime,
          instructor_id: instructorId,
        }),
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": this.getCSRFToken(),
          Accept: "text/vnd.turbo-stream.html",
        },
      });

      if (response.ok) {
        const quickViewElement = document.querySelector(
          "#school-dashboard-lesson-quick-look"
        );
        quickViewElement.classList.remove("u-hidden");

        const responseBody = await response.text();
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(responseBody, "text/html");
        const template = xmlDoc.querySelector("template");
        const targetElement = document.getElementById("school_lesson_quick_look");

        if (template && targetElement) {
          targetElement.innerHTML = "";
          targetElement.appendChild(template.content.cloneNode(true));
        }
      }
    } finally {
      this.finishLoadingState(loadingState);
    }
  }

  async deleteSlot(event) {
    event.preventDefault();

    let button = event.currentTarget;
    let spinner = button.querySelector(".Spinner");
    spinner.classList.remove("u-hidden");
    spinner.classList.add("u-inlineBlock");
    button.disabled = true;

    let deleteSlotValidationMessage = document.getElementById(
      "delete-slot-validation-message"
    );
    deleteSlotValidationMessage.hidden = true;

    const slotId = this.data.get("slotId");
    const startTimeForDelete = this.data.get("startTime");
    const endTimeForDelete = this.data.get("endTime");
    const instructorIdForDelete = this.data.get("instructorId");

    const urlForDelete = `/schools/bookings/delete-slot?slot=true&start_time=${startTimeForDelete}&end_time=${endTimeForDelete}&instructor_id=${instructorIdForDelete}&slot_id=${slotId}`;

    const response = await fetch(urlForDelete, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-CSRF-Token": this.getCSRFToken(),
        Accept: "text/vnd.turbo-stream.html",
      },
    });

    if (response.ok) {
      const quickViewElement = document.querySelector(
        "#school-dashboard-lesson-quick-look"
      );
      quickViewElement.classList.remove("u-hidden");

      const responseBody = await response.text();
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(responseBody, "text/html");
      const template = xmlDoc.querySelector("template");
      const targetElement = document.getElementById("school_lesson_quick_look");

      if (template && targetElement) {
        targetElement.innerHTML = "";
        targetElement.appendChild(template.content.cloneNode(true));
      }
    } else {
      let deleteSlotValidationMessage = document.getElementById(
        "delete-slot-validation-message"
      );
      deleteSlotValidationMessage.hidden = false;
      let spinner = button.querySelector(".Spinner");
      spinner.classList.add("u-hidden");
      spinner.classList.remove("u-inlineBlock");
      this.element.disabled = false;
    }
  }

  async editSlotOpenQuickLook(event) {
    event.preventDefault();
    const loadingState = this.startLoadingState(event.currentTarget || this.element);
    try {
      const response = await fetch(this.element.getAttribute("href"), {
        method: "GET",
        headers: {
          "X-CSRF-Token": this.getCSRFToken(),
          Accept: "text/vnd.turbo-stream.html",
        },
      });

      if (response.ok) {
        const quickViewElement = document.querySelector(
          "#school_lesson_quick_look"
        );
        quickViewElement.classList.remove("u-hidden");

        const responseBody = await response.text();
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(responseBody, "text/html");
        const template = xmlDoc.querySelector("template");
        const targetElement = document.getElementById("school_lesson_quick_look");

        if (template && targetElement) {
          targetElement.innerHTML = "";
          targetElement.appendChild(template.content.cloneNode(true));
        }
      }
    } finally {
      this.finishLoadingState(loadingState);
    }
  }

  getCSRFToken() {
    const metaTag = document.querySelector("meta[name='csrf-token']");
    return metaTag ? metaTag.getAttribute("content") : "";
  }

  async bookDriveTest(event) {
    event.preventDefault();
    const instructorId = document.getElementById("instructor_id").value;
    const startTime = document.getElementById("start_time").value;

    let testTime = this.data.get("testTime") || "";
    let slotId = this.data.get("slotId") || "nil";
    let isTestSlot = this.data.get("isTestSlot") === "true";
    let slotPayload = this.data.get("slotPayload") || "";
    let precticalTestTime = "";

    let url = `/schools/open-slot/${instructorId}/quick-look/book-slot/practical-test/start-time/${startTime}?slot_id=${encodeURIComponent(
      slotId
    )}&is_test_slot=${isTestSlot}`;

    if (slotPayload) {
      url += `&slot_payload=${encodeURIComponent(slotPayload)}`;
    }

    if (testTime && testTime.includes("T")) {
      precticalTestTime = testTime
        .split("T")[1]
        .split(":")
        .filter((_, i) => i < 2)
        .join(":");
      url += `&practical_test_time=${precticalTestTime}`;
    } else if (testTime && testTime.includes(":")) {
      precticalTestTime = testTime
        .split(":")
        .filter((_, i) => i < 2)
        .join(":");
      url += `&practical_test_time=${precticalTestTime}`;
    }

    await this.fetchData(url, instructorId, startTime, true, event.currentTarget);
  }

  async cancelOptionsAction(event) {
    event.preventDefault();
    const instructorId = document.getElementById("instructor_id").value;
    const startTime = document.getElementById("start_time").value;
    const url = event.currentTarget.getAttribute("href");
    const urlObject = new URL(url, window.location.origin);
    const isPracticalTest = urlObject.searchParams.get("is_test_slot") === "true";

    await this.fetchData(url, instructorId, startTime, isPracticalTest);
  }

  followTurboAction(event) {
    this.startLoadingState(event.currentTarget);
    window.setTimeout(() => this.finishLoadingStateForElement(event.currentTarget), 15000);
  }

  async fetchData(url, instructorId, startTime, isPracticalTest, triggerElement = null) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content;
    const loadingState = this.startLoadingState(triggerElement || this.element);

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": csrfToken,
          Accept: "text/vnd.turbo-stream.html",
        },
      });

      if (response.ok) {
        const stream = await response.text();
        Turbo.renderStreamMessage(stream);
      }

      const updateSlotDetailsUrl = `/schools/open-slot/${instructorId}/quick-look/update-slot-details/start-time/${startTime}?is_practical_test=${isPracticalTest}`;
      const updateSlotDetailsResponse = await fetch(updateSlotDetailsUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": csrfToken,
          Accept: "text/vnd.turbo-stream.html",
        },
      });

      if (updateSlotDetailsResponse.ok) {
        const stream = await updateSlotDetailsResponse.text();
        Turbo.renderStreamMessage(stream);
      }
    } finally {
      this.finishLoadingState(loadingState);
    }
  }

  startLoadingState(element) {
    if (!element || element.dataset.loadingStateActive === "true") {
      return null;
    }

    element.dataset.loadingStateActive = "true";
    element.dataset.originalHtml = element.innerHTML;
    element.dataset.originalPointerEvents = element.style.pointerEvents || "";
    element.dataset.originalOpacity = element.style.opacity || "";
    element.setAttribute("aria-disabled", "true");
    element.classList.add("is-disabled");
    element.style.pointerEvents = "none";
    element.style.opacity = "0.7";

    if ("disabled" in element) {
      element.disabled = true;
    }

    element.innerHTML = `Loading ${this.spinnerMarkup()}`;

    return { element };
  }

  finishLoadingState(state) {
    if (!state || !state.element) {
      return;
    }

    this.finishLoadingStateForElement(state.element);
  }

  finishLoadingStateForElement(element) {
    if (!element || element.dataset.loadingStateActive !== "true") {
      return;
    }

    element.innerHTML = element.dataset.originalHtml || element.innerHTML;
    delete element.dataset.originalHtml;
    delete element.dataset.loadingStateActive;
    element.style.pointerEvents = element.dataset.originalPointerEvents || "";
    element.style.opacity = element.dataset.originalOpacity || "";
    delete element.dataset.originalPointerEvents;
    delete element.dataset.originalOpacity;
    element.removeAttribute("aria-disabled");
    element.classList.remove("is-disabled");

    if ("disabled" in element) {
      element.disabled = false;
    }
  }

  spinnerMarkup() {
    return `
      <svg class="Spinner u-inlineBlock" width="16px" height="16px" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid">
        <circle cx="50" cy="50" fill="none" stroke="#ffffff" stroke-width="14" r="40" stroke-dasharray="188.49555921538757 64.83185307179586">
          <animateTransform attributeName="transform" type="rotate" repeatCount="indefinite" dur="1s" values="0 50 50;360 50 50" keyTimes="0;1"></animateTransform>
        </circle>
      </svg>
    `;
  }
}

function optionalChaining(valueFn) {
  try {
    return valueFn();
  } catch (e) {
    return undefined;
  }
}
